import { getStampString } from '../config/version';
import CrawlerVisionLogo from './CrawlerVisionLogo';

const Footer = () => {
  return (
    <footer className="bg-cv-navy text-white/85">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-7 mb-8">
          {/* Column 1: About */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <h3 className="text-lg font-bold mb-3">
              <a href="/" className="inline-block hover:opacity-80 transition-opacity" title="Crawler Vision (#CV)">
                <CrawlerVisionLogo variant="onDark" className="h-10 w-auto max-w-[240px] object-contain" />
                <span className="sr-only">Crawler Vision (#CV)</span>
              </a>
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              Professional website analysis platform for SEO optimization and AI crawler readiness assessment.
              Helping businesses improve their search visibility and AI discoverability.
            </p>
            <p className="text-white/60 text-xs">
              © 2025-{new Date().getFullYear()} Crawler Vision. All rights reserved.
            </p>
            <p
              className="text-white/60 text-xs font-mono mt-1 break-all leading-relaxed"
              title={`Build stamp · compare against /build-stamp.json or the X-CV-Build response header`}
            >
              {getStampString()}
            </p>
          </div>

          {/* Column 2: Resources */}
          <div>
            <h4 className="text-base font-semibold mb-3">Resources</h4>
            <ul className="space-y-1.5">
              <li><a href="/learning" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🌱 Learning Center</a></li>
              <li><a href="/knowledge" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📚 Knowledge Base</a></li>
              <li><a href="/faq" className="text-white/70 hover:text-cv-gold transition-colors text-sm">❓ FAQ</a></li>
              <li><a href="/releases" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📰 Release Notes</a></li>
              <li><a href="/dashboard-guide" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📊 Dashboard Guide</a></li>
              <li><a href="/report-guide" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📄 Report Guide</a></li>
              <li><a href="/scoring" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📐 Scoring Methodology</a></li>
              <li><a href="/keyword-intelligence" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🔎 Keyword Intelligence</a></li>
              <li><a href="/bot" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🤖 Our Bot</a></li>
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h4 className="text-base font-semibold mb-3">Legal</h4>
            <ul className="space-y-1.5">
              <li><a href="/terms" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📜 Terms</a></li>
              <li><a href="/privacy" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🔒 Privacy</a></li>
              <li><a href="/cookies" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🍪 Cookies</a></li>
              <li><a href="/disclaimer" className="text-white/70 hover:text-cv-gold transition-colors text-sm">⚠️ Disclaimer</a></li>
              <li><a href="/acceptable-use" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📋 Acceptable Use</a></li>
              <li><a href="/supporters-terms" className="text-white/70 hover:text-cv-gold transition-colors text-sm">💝 Supporters T&C</a></li>
            </ul>
          </div>

          {/* Column 4: Policies & Contact */}
          <div>
            <h4 className="text-base font-semibold mb-3">Policies & Contact</h4>
            <ul className="space-y-1.5">
              <li><a href="/refunds" className="text-white/70 hover:text-cv-gold transition-colors text-sm">💰 Refund Policy</a></li>
              <li><a href="/shipping" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📦 Shipping Policy</a></li>
              <li><a href="/contact" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📧 Contact Us</a></li>
              <li><a href="/about" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🏢 About Us</a></li>
            </ul>
          </div>

          {/* Column 5: Partners */}
          <div>
            <h4 className="text-base font-semibold mb-3">Partners</h4>
            <ul className="space-y-1.5">
              <li><a href="/partner-program" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🤝 Partner Program</a></li>
              <li><a href="/partner-knowledge" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📚 Partner Knowledge Base</a></li>
              <li><a href="/partner-faq" className="text-white/70 hover:text-cv-gold transition-colors text-sm">❓ Partner FAQ</a></li>
              <li><a href="/partner-terms" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📜 Partner T&C</a></li>
              <li><a href="/partner-disclaimer" className="text-white/70 hover:text-cv-gold transition-colors text-sm">⚠️ Partner Disclaimer</a></li>
              <li><a href="/partner-agreement" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📝 Partner Agreement</a></li>
              <li><a href="/partner" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🚀 Partner Portal</a></li>
            </ul>
          </div>

          {/* Column 5: Application */}
          <div>
            <h4 className="text-base font-semibold mb-3">Application</h4>
            <ul className="space-y-1.5">
              <li><a href="/analyzer" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🚀 Launch Analyzer</a></li>
              <li><a href="/dashboard" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📊 Dashboard</a></li>
              <li><a href="/health" className="text-white/70 hover:text-cv-gold transition-colors text-sm">🟢 System Health</a></li>
              <li><a href="/api-docs" className="text-white/70 hover:text-cv-gold transition-colors text-sm">📖 API Docs</a></li>
              <li><a href="/pricing" className="text-white/70 hover:text-cv-gold transition-colors text-sm">💎 Pricing</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/15 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-3 text-sm text-white/60">
              <span>🌐 crawlervision.com</span>
              <span>|</span>
              <span>📧 support@crawlervision.com</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <span className="bg-cv-teal w-2 h-2 rounded-full animate-pulse"></span>
              <span>All systems operational</span>
            </div>
          </div>
          <div className="text-center mt-4 text-xs text-white/60">
            A Product Of <a href="https://dnsstack.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">DNS Stack Pvt Ltd</a> and <a href="https://techtonic.systems" target="_blank" rel="noopener noreferrer" className="hover:text-white">Techtonic Systems Media and Research LLC</a>
          </div>
          <div className="mt-4 pt-4 border-t border-white/15 text-center text-xs text-white/60">
            <span>🤖 For Bots & Crawlers:</span>
            <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="ml-2 hover:text-white">llms.txt</a> ·{' '}
            <a href="/ai.txt" target="_blank" rel="noopener noreferrer" className="hover:text-white">ai.txt</a> ·{' '}
            <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="hover:text-white">robots.txt</a> ·{' '}
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-white">sitemap.xml</a> ·{' '}
            <a href="/manifest.json" target="_blank" rel="noopener noreferrer" className="hover:text-white">manifest.json</a> ·{' '}
            <a href="/brand.json" target="_blank" rel="noopener noreferrer" className="hover:text-white">brand.json</a> ·{' '}
            <a href="/context.json" target="_blank" rel="noopener noreferrer" className="hover:text-white">context.json</a>
          </div>
          <div className="text-center mt-2 text-[11px] text-white/60 flex items-center justify-center gap-1.5">
            <span className="inline-block w-3 h-3 text-cv-teal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </span>
            <span>DPDP Act 2023 Aligned — Public data only · Right to erasure · Only accounting &amp; tax records retained · No external sharing</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
